import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  buyerAllocationApi,
  type BuyerAllocationOverrideDto,
  type BuyerAllocationTeamTierDto,
  type BuyerAllocationValuesDto,
  type ResolvedBuyerAllocation,
  type SaveBuyerAllocationPayload,
} from "@/api/buyers";
import { extractErrorMessage } from "@/api/client";
import { BuyerAllocationEditorModal } from "@/components/buyers/BuyerAllocationEditorModal";
import { StaleContactDaysField } from "./teams/StaleContactDaysField";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  Button,
  EmptyState,
  MissingValue,
  Skeleton,
  StatCard,
  StatsBar,
  SubTable,
  Timestamp,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { buyerAllocationKeys } from "@/hooks/buyers/buyerKeys";
import {
  useBuyerAllocationSettings,
  useBuyerEntitlements,
} from "@/hooks/buyers/useBuyerAllocation";
import { useAuth } from "@/hooks/useAuth";
import { useDialogs } from "@/hooks/useDialogs";
import { QueryErrorState } from "./queryStates";

const MANAGE_PERMISSION = "Buyers.ManageAllocation";

/** Which editor is open, and on whom. */
type Editing =
  | { kind: "global" }
  | { kind: "override"; specialistUserId: string; displayName: string }
  | null;

/**
 * The allocation policy (PR 2b) — how many buyers a specialist EARNS, as
 * distinct from which buyer a manager hands to whom.
 *
 * <p>Two layers with in-code defaults underneath: a global policy, and a
 * per-specialist override. Every authored value is nullable and null means
 * INHERIT, so this screen has to show BOTH what was written down and what it
 * actually resolves to — a table of authored values alone cannot be read, and a
 * table of effective values alone hides where each number came from.</p>
 *
 * <p>Gated <c>Buyers.ManageAllocation</c> — F1 and ADMIN. The route already
 * requires it; the guard below is defense-in-depth, following the
 * card-surcharge precedent.</p>
 */
export function BuyerAllocationSettingsPage() {
  const { hasPermission } = useAuth();
  const qc = useQueryClient();
  const dialogs = useDialogs();

  const [editing, setEditing] = useState<Editing>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const settings = useBuyerAllocationSettings(hasPermission(MANAGE_PERMISSION));

  // The specialist roster for "add an override". The entitlement read is the
  // only list that already knows who the buyer consultants are, and it is
  // Buyers.Read — which F1 holds via the Buyer CRM augment.
  const entitlements = useBuyerEntitlements(undefined, hasPermission(MANAGE_PERMISSION));

  const save = useMutation({
    mutationFn: (vars: { specialistUserId: string | null; body: SaveBuyerAllocationPayload }) =>
      vars.specialistUserId === null
        ? buyerAllocationApi.saveGlobal(vars.body)
        : buyerAllocationApi.saveOverride(vars.specialistUserId, vars.body),
    onSuccess: async () => {
      setEditing(null);
      setSaveError(null);
      // The policy is the input to every entitlement, and entitlement is
      // recomputed on read — so a policy write restates every standing on the
      // manager's queue and in each specialist's own widget.
      await qc.invalidateQueries({ queryKey: buyerAllocationKeys.all });
    },
    // Surfaced verbatim rather than pre-empted: the server owns the rules (a
    // threshold of zero is a division by zero, not an "off"), and its message
    // already says what to do about it.
    onError: (e: unknown) => setSaveError(extractErrorMessage(e)),
  });

  const clearOverride = useMutation({
    mutationFn: (specialistUserId: string) =>
      buyerAllocationApi.clearOverride(specialistUserId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: buyerAllocationKeys.all });
    },
    onError: (e: unknown) =>
      void dialogs.alert({
        title: "Couldn't remove the override",
        tone: "error",
        message: extractErrorMessage(e),
      }),
  });

  if (!hasPermission(MANAGE_PERMISSION)) {
    return (
      <>
        <PageHeader eyebrow="Buyer CRM" title="Allocation policy" />
        <EmptyState
          title="Not authorized"
          description="You don't have permission to manage the buyer allocation policy."
        />
      </>
    );
  }

  const data = settings.data;
  const overrides = data?.overrides ?? [];
  const teamTiers = data?.teamTiers ?? [];
  // 🔴 Server-resolved: the global row is written by all-teams holders only,
  // and a one-team manager's `saveGlobal` is a 403. Optional on the wire for
  // the fixtures' sake; the server always sends it, and an absent value reads
  // as the C# default (true) rather than as a refusal.
  const canEditGlobal = data?.canEditGlobal !== false;
  const overridden = new Set(overrides.map((o) => o.specialistUserId));
  const addable = (entitlements.data ?? []).filter(
    (e) => !overridden.has(e.specialistUserId),
  );

  const resetGlobal = async () => {
    const ok = await dialogs.confirm({
      title: "Reset to shipped defaults?",
      destructive: true,
      confirmLabel: "Reset",
      message:
        "Clears every value authored on the global policy. Consultants fall back to the shipped defaults, and per-consultant overrides are left alone.",
    });
    if (!ok) return;
    save.mutate({
      specialistUserId: null,
      // 🔴 All-null is how "reset to inherited" is spelled — it DELETES the row.
      // Zeros would author a policy of zero, which is a different thing.
      body: {
        startingAllocation: null,
        qualifyingThreshold: null,
        maxActiveBuyers: null,
        notes: null,
      },
    });
  };

  const resetOverride = async (o: BuyerAllocationOverrideDto) => {
    const ok = await dialogs.confirm({
      title: "Reset to the global policy?",
      destructive: true,
      confirmLabel: "Reset",
      message: `Removes the override for ${o.displayName ?? o.specialistUserId}. They follow the global policy again.`,
    });
    if (ok) clearOverride.mutate(o.specialistUserId);
  };

  return (
    <>
      <PageHeader
        eyebrow="Buyer CRM"
        title="Allocation policy"
        description="How many buyers a consultant earns: a starting allocation, plus one more for every N qualifying submissions, optionally capped. Blank means inherit — never zero."
        actions={
          canEditGlobal ? (
            <Button variant="secondary" onClick={() => setEditing({ kind: "global" })}>
              Edit global policy
            </Button>
          ) : undefined
        }
      />

      {settings.isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : settings.isError || !data ? (
        /* Said "Try again" and gave nothing to try again WITH — and with the
           shared QueryClient at `staleTime: 5min` and no refetch on focus, a
           full page reload was the only route back. */
        <QueryErrorState
          what="the allocation policy"
          onRetry={() => void settings.refetch()}
          retrying={settings.isFetching}
        />
      ) : (
        <>
          <StatsBar columns={3}>
            <StatCard label="Starting allocation" value={data.effectiveDefaults.startingAllocation} />
            <StatCard
              label="Qualifying submissions per buyer"
              value={data.effectiveDefaults.qualifyingThreshold}
            />
            <StatCard
              label="Cap on active buyers"
              value={data.effectiveDefaults.maxActiveBuyers ?? "None"}
            />
          </StatsBar>

          <section className="mb-space-5">
            <h2 className="font-display text-h3 text-onyx mb-space-2">Global policy</h2>
            {/* Authored beside effective, because "3" on its own does not say
                whether somebody chose it or it fell through from the defaults —
                and that is the only question this screen exists to answer. */}
            <div className="border border-border-light rounded-md bg-white-warm p-space-4 flex flex-col gap-space-3">
              <AuthoredRow
                label="Starting allocation"
                authored={data.global.startingAllocation}
                effective={data.effectiveDefaults.startingAllocation}
              />
              <AuthoredRow
                label="Qualifying threshold"
                authored={data.global.qualifyingThreshold}
                effective={data.effectiveDefaults.qualifyingThreshold}
              />
              <AuthoredRow
                label="Cap on active buyers"
                authored={data.global.maxActiveBuyers}
                effective={data.effectiveDefaults.maxActiveBuyers}
                isCap
              />
              {/* D10 — the platform-wide stale window every team inherits
                  unless its own tier says otherwise. The wire carries no
                  separately resolved global value, so an unauthored row reads
                  as "platform default" rather than as a number nobody sent. */}
              <StaleContactDaysField
                authored={data.global.staleContactDays}
                canEdit={canEditGlobal}
                saving={save.isPending}
                onSave={(days) =>
                  save.mutate({
                    specialistUserId: null,
                    body: { ...authoredGlobal(data.global), staleContactDays: days },
                  })
                }
                testIdPrefix="allocation-global"
              />
              {data.global.notes && (
                <p className="text-caption text-gray-mid">{data.global.notes}</p>
              )}
              {canEditGlobal ? (
                <div>
                  <Button size="sm" variant="text" onClick={resetGlobal} disabled={save.isPending}>
                    Reset to shipped defaults
                  </Button>
                </div>
              ) : (
                /* Prose naming who can, not a button that would 403. */
                <p className="text-caption text-gray-mid" data-testid="allocation-global-readonly">
                  The global policy is set by someone with all-teams authority. Your
                  team&apos;s own tier is edited on its Allocation tab.
                </p>
              )}
            </div>
          </section>

          <section className="mb-space-5">
            <h2 className="font-display text-h3 text-onyx mb-space-2">Team tiers</h2>
            {/* Read here, edited on the team: a tier is the team's own record,
                and its Allocation tab is where the standings it governs are. */}
            {teamTiers.length === 0 ? (
              <EmptyState
                title="No team tiers"
                description="Every team you dispatch follows the global policy. A team's tier is edited on its Allocation tab."
              />
            ) : (
              <SubTable<BuyerAllocationTeamTierDto>
                caption="Teams whose allocation differs from the global policy"
                data-testid="allocation-team-tiers"
                rows={teamTiers}
                rowKey={(t) => t.buyerTeamId}
                columns={teamTierColumns}
              />
            )}
          </section>

          <section>
            <div className="flex flex-wrap items-center justify-between gap-space-2 mb-space-2">
              <h2 className="font-display text-h3 text-onyx">Per-consultant overrides</h2>
              <AddOverride
                candidates={addable}
                disabled={save.isPending}
                failed={entitlements.isError}
                onRetry={() => void entitlements.refetch()}
                onPick={(specialistUserId, displayName) =>
                  setEditing({ kind: "override", specialistUserId, displayName })
                }
              />
            </div>

            {overrides.length === 0 ? (
              <EmptyState
                title="No overrides"
                description="Every consultant follows the global policy."
              />
            ) : (
              <SubTable<BuyerAllocationOverrideDto>
                caption="Consultants whose allocation differs from the global policy"
                data-testid="allocation-overrides"
                rows={overrides}
                rowKey={(o) => o.specialistUserId}
                columns={overrideColumns(setEditing, resetOverride, clearOverride.isPending)}
              />
            )}
          </section>
        </>
      )}

      {data && editing?.kind === "global" && (
        <BuyerAllocationEditorModal
          open
          onClose={() => {
            setEditing(null);
            setSaveError(null);
          }}
          title="Global allocation policy"
          inherited={SHIPPED_DEFAULTS}
          inheritedLabel="the shipped defaults"
          values={data.global}
          saving={save.isPending}
          error={saveError}
          // 🔴 The shared editor knows nothing about the stale window (it is
          // also the override's editor, where the server refuses the field),
          // so its save is re-merged with what the global row already authored
          // — otherwise editing the three numbers silently clears the fourth.
          onSave={(body) =>
            save.mutate({
              specialistUserId: null,
              body: { ...body, staleContactDays: data.global.staleContactDays ?? null },
            })
          }
        />
      )}

      {data && editing?.kind === "override" && (
        <BuyerAllocationEditorModal
          open
          onClose={() => {
            setEditing(null);
            setSaveError(null);
          }}
          title="Consultant override"
          subject={editing.displayName}
          inherited={data.effectiveDefaults}
          inheritedLabel="the global policy"
          values={
            overrides.find((o) => o.specialistUserId === editing.specialistUserId)?.authored
          }
          saving={save.isPending}
          error={saveError}
          onSave={(body) =>
            save.mutate({ specialistUserId: editing.specialistUserId, body })
          }
        />
      )}
    </>
  );
}

/**
 * The in-code floor, mirrored so the global editor can say what "inherit"
 * resolves to. The server never returns it separately: `effectiveDefaults` IS
 * the global row already resolved against these, so on an unauthored global row
 * the two are identical — and once somebody authors a value, this is the only
 * way to show what clearing it would go back to.
 */
const SHIPPED_DEFAULTS: ResolvedBuyerAllocation = {
  startingAllocation: 3,
  qualifyingThreshold: 10,
  maxActiveBuyers: null,
};

/** The global row's three numbers and its notes, as a save body — so a write
 *  of ONE field (the stale window) restates the rest verbatim instead of
 *  clearing them. Null means inherit, exactly as the row stores it. */
function authoredGlobal(g: BuyerAllocationValuesDto): SaveBuyerAllocationPayload {
  return {
    startingAllocation: g.startingAllocation ?? null,
    qualifyingThreshold: g.qualifyingThreshold ?? null,
    maxActiveBuyers: g.maxActiveBuyers ?? null,
    notes: g.notes ?? null,
  };
}

export function AuthoredRow({
  label,
  authored,
  effective,
  isCap = false,
}: {
  label: string;
  authored: number | null | undefined;
  effective: number | null | undefined;
  isCap?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-baseline gap-space-2">
      <span className="text-caption uppercase tracking-wide text-gray-mid">{label}</span>
      <Cell authored={authored} effective={effective} isCap={isCap} />
    </div>
  );
}

/**
 * One value, said twice when the two differ: what is authored here, and what it
 * resolves to. A cap is the awkward one — non-positive is "explicitly none",
 * which reads the same as an inherited none unless it is spelled out.
 */
export function Cell({
  authored,
  effective,
  isCap = false,
}: {
  authored: number | null | undefined;
  effective: number | null | undefined;
  isCap?: boolean;
}) {
  const effectiveText = isCap ? (effective == null ? "no cap" : String(effective)) : String(effective);

  if (authored == null) {
    return (
      <span className="text-onyx">
        {effectiveText}{" "}
        <span className="text-caption text-gray-mid">(inherited)</span>
      </span>
    );
  }

  const authoredText = isCap && authored <= 0 ? "no cap" : String(authored);
  return (
    <span className="text-onyx">
      {authoredText} <span className="text-caption text-gray-mid">(set here)</span>
    </span>
  );
}

/**
 * One team's tier, read-only. The door is the team's Allocation tab — the
 * tier is the team's record, and that tab is where the standings it governs are
 * read beside it.
 */
const teamTierColumns: SubTableColumn<BuyerAllocationTeamTierDto>[] = [
  {
    key: "team",
    header: "Team",
    render: (t) => (
      <div className="min-w-0">
        <Link
          to={`/buyers/teams/${t.buyerTeamId}?tab=allocation`}
          className="font-semibold text-gold-shadow hover:underline"
        >
          {t.buyerTeamName}
        </Link>
        {t.authored.notes && (
          <div className="text-caption text-gray-mid">{t.authored.notes}</div>
        )}
      </div>
    ),
  },
  {
    key: "starting",
    header: "Starting",
    render: (t) => (
      <Cell authored={t.authored.startingAllocation} effective={t.effective.startingAllocation} />
    ),
  },
  {
    key: "threshold",
    header: "Threshold",
    render: (t) => (
      <Cell authored={t.authored.qualifyingThreshold} effective={t.effective.qualifyingThreshold} />
    ),
  },
  {
    key: "cap",
    header: "Cap",
    render: (t) => (
      <Cell authored={t.authored.maxActiveBuyers} effective={t.effective.maxActiveBuyers} isCap />
    ),
  },
  {
    key: "updated",
    header: "Updated",
    render: (t) => (t.modifiedAt ? <Timestamp value={t.modifiedAt} /> : <MissingValue />),
  },
];

function AddOverride({
  candidates,
  disabled,
  onPick,
  failed,
  onRetry,
}: {
  candidates: { specialistUserId: string; displayName?: string | null }[];
  disabled: boolean;
  onPick: (specialistUserId: string, displayName: string) => void;
  /** The specialist list failed to load — distinct from there being nobody. */
  failed?: boolean;
  onRetry?: () => void;
}) {
  const [choice, setChoice] = useState("");

  // The candidates come from a SEPARATE read, so a failure here leaves the rest
  // of the page correct while this one control silently vanishes — a manager
  // reads that as "there is nobody left to override", which it is not.
  if (failed) {
    return (
      <Button size="sm" variant="secondary" onClick={onRetry}>
        Couldn&apos;t load consultants — try again
      </Button>
    );
  }

  if (candidates.length === 0) return null;

  const pick = () => {
    const found = candidates.find((c) => c.specialistUserId === choice);
    if (!found) return;
    onPick(found.specialistUserId, found.displayName ?? found.specialistUserId);
    setChoice("");
  };

  return (
    <div className="flex items-center gap-space-2">
      <select
        value={choice}
        onChange={(e) => setChoice(e.target.value)}
        aria-label="Consultant to override"
        className="h-10 rounded-md border border-border-light bg-white-pure px-space-2 text-small"
      >
        <option value="">Add an override…</option>
        {candidates.map((c) => (
          <option key={c.specialistUserId} value={c.specialistUserId}>
            {c.displayName ?? c.specialistUserId}
          </option>
        ))}
      </select>
      <Button size="sm" disabled={disabled || choice === ""} onClick={pick}>
        Add
      </Button>
    </div>
  );
}

/**
 * One specialist's override.
 *
 * 🔴 THE ROWS STAY RICH. Each policy cell renders through `Cell`, which shows
 * an authored value against the effective one — the point of this screen is the
 * difference between what somebody typed and what the system is actually doing,
 * and a table showing only the effective number would answer the wrong
 * question. The identity cell keeps its authoring note, and the last cell keeps
 * both of the row's actions.
 */
function overrideColumns(
  onEdit: (target: { kind: "override"; specialistUserId: string; displayName: string }) => void,
  onReset: (o: BuyerAllocationOverrideDto) => void,
  resetPending: boolean,
): SubTableColumn<BuyerAllocationOverrideDto>[] {
  return [
    {
      key: "specialist",
      header: "Consultant",
      render: (o) => (
        <div className="min-w-0">
          {o.displayName ?? o.specialistUserId}
          {o.authored.notes && (
            <div className="text-caption text-gray-mid">{o.authored.notes}</div>
          )}
        </div>
      ),
    },
    {
      key: "starting",
      header: "Starting",
      render: (o) => (
        <Cell authored={o.authored.startingAllocation} effective={o.effective.startingAllocation} />
      ),
    },
    {
      key: "threshold",
      header: "Threshold",
      render: (o) => (
        <Cell
          authored={o.authored.qualifyingThreshold}
          effective={o.effective.qualifyingThreshold}
        />
      ),
    },
    {
      key: "cap",
      header: "Cap",
      render: (o) => (
        <Cell authored={o.authored.maxActiveBuyers} effective={o.effective.maxActiveBuyers} isCap />
      ),
    },
    {
      key: "updated",
      header: "Updated",
      render: (o) => (o.modifiedAt ? <Timestamp value={o.modifiedAt} /> : <MissingValue />),
    },
    {
      key: "actions",
      header: "Actions",
      searchText: () => "",
      render: (o) => (
        <div className="flex items-center gap-space-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() =>
              onEdit({
                kind: "override",
                specialistUserId: o.specialistUserId,
                displayName: o.displayName ?? o.specialistUserId,
              })
            }
          >
            Edit
          </Button>
          <Button size="sm" variant="text" disabled={resetPending} onClick={() => onReset(o)}>
            Reset to global
          </Button>
        </div>
      ),
    },
  ];
}
